import React, { useEffect, useState } from "react";
import { Input, Modal } from "antd";
import { Eye, MessageSquareText, RotateCcw } from "lucide-react";
import {
  useAllOrders,
  useReverifyOrder,
  useUpdatePaymentMethod,
  useDeletePaymentImage,
} from "../../../../../../api/orders/orders";

const ORDERS_PER_PAGE = 20;

const ReverifyOrder = () => {
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRemarkModalOpen, setIsRemarkModalOpen] = useState(false);
  const [selectedRemark, setSelectedRemark] = useState("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const [editPayments, setEditPayments] = useState([]);

  const BACKEND_URL = "https://sr.teamexbiz.in/api";

  // Mutations
  const { mutate: reverifyOrder, isLoading: isReverifying } =
    useReverifyOrder();
  const { mutate: updatePaymentMethod, isLoading: isUpdating } =
    useUpdatePaymentMethod();
  const { mutate: deletePaymentImage, isLoading: isDeleting } =
    useDeletePaymentImage();

  // Fetch orders
  const {
    data: allOrdersData,
    isLoading: isAllLoading,
    isError,
    isFetching,
  } = useAllOrders({
    currentPage,
    ORDERS_PER_PAGE,
    verify: false,
    decline: true,
    search: searchQuery,
  });

  // Debounce search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearchQuery(searchText.trim());
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchText]);

const orders = (allOrdersData?.orders || []).filter(
  (order) => order.verify || order.decline
);
  const totalPages = allOrdersData?.totalPages || 1;
  const isLoading = isAllLoading;

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  const handleViewRemark = (remark) => {
    setSelectedRemark(remark || "No remark available");
    setIsRemarkModalOpen(true);
  };

  const handleReverify = (order) => {
    setEditOrder(order);
    setEditPayments(
      order.paymentMethods?.length > 0
        ? order.paymentMethods.map((m, idx) => ({
            _id: m._id,
            methodIndex: idx,
            paymentOption: m.paymentOption,
            paymentAmount: m.paymentAmount ,
            paymentProofImages: m.paymentProofImages ,
            newFiles: [], // Track new files to upload
          }))
        : []
    );
    setIsEditModalOpen(true);
  };

  const handlePaymentChange = (index, field, value) => {
    const updated = [...editPayments];
    updated[index][field] = value;
    setEditPayments(updated);
  };

  const handleImageUpload = (index, e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const updated = [...editPayments];

      // Create local URLs for preview
      const newImagePreviews = files.map((file) => URL.createObjectURL(file));

      // Add previews to display
      updated[index].paymentProofImages = [
        ...(updated[index].paymentProofImages ),
        ...newImagePreviews,
      ];

      // Store actual files for upload
      updated[index].newFiles = [...(updated[index].newFiles ), ...files];

      setEditPayments(updated);
    }
  };

  const handleRemoveImage = (paymentIndex, imageUrl) => {
    const method = editPayments[paymentIndex];

    // Check if it's a new image (blob URL) or existing image
    if (imageUrl.startsWith("blob:")) {
      // It's a newly uploaded image - just remove from local state
      const updated = [...editPayments];
      updated[paymentIndex].paymentProofImages = updated[
        paymentIndex
      ].paymentProofImages.filter((img) => img !== imageUrl);
      // Also remove from newFiles if it exists
      const fileIndex = method.newFiles?.findIndex(
        (f) => URL.createObjectURL(f) === imageUrl
      );
      if (fileIndex !== -1) {
        updated[paymentIndex].newFiles.splice(fileIndex, 1);
      }
      setEditPayments(updated);
      URL.revokeObjectURL(imageUrl); // Clean up blob URL
    } else {
      // It's an existing image - call API to delete
      deletePaymentImage(
        {
          orderId: editOrder._id,
          methodIndex: paymentIndex,
          imageUrl: imageUrl,
        },
        {
          onSuccess: () => {
            // Remove from local state after successful deletion
            const updated = [...editPayments];
            updated[paymentIndex].paymentProofImages = updated[
              paymentIndex
            ].paymentProofImages.filter((img) => img !== imageUrl);
            setEditPayments(updated);
          },
        }
      );
    }
  };

  const handleSavePaymentMethod = async (methodIndex) => {
    const method = editPayments[methodIndex];

    updatePaymentMethod(
      {
        orderId: editOrder._id,
        methodIndex: methodIndex,
        paymentOption: method.paymentOption,
        paymentAmount: method.paymentAmount,
        paymentProofImages: method.newFiles ,
      },
      {
        onSuccess: () => {
          // Clear new files after successful upload
          const updated = [...editPayments];
          updated[methodIndex].newFiles = [];
          setEditPayments(updated);
        },
      }
    );
  };

  const handleCloseEditModal = () => {
    // Clean up blob URLs
    editPayments.forEach((method) => {
      method.paymentProofImages?.forEach((img) => {
        if (img.startsWith("blob:")) {
          URL.revokeObjectURL(img);
        }
      });
    });
    setIsEditModalOpen(false);
  };

  const getTotalPaymentAmount = () => {
    return editPayments.reduce(
      (sum, method) => sum + (parseFloat(method.paymentAmount) || 0),
      0
    );
  };

  const getPaymentByOption = (order, optionName) => {
    if (!order.paymentMethods || order.paymentMethods.length === 0) return 0;
    const method = order.paymentMethods.find(
      (m) => m.paymentOption?.toLowerCase() === optionName.toLowerCase()
    );
    return method ? method.paymentAmount || 0 : 0;
  };

  const getAllPaymentImages = (order) => {
    if (order.paymentMethods && order.paymentMethods.length > 0) {
      const allImages = [];
      order.paymentMethods.forEach((method) => {
        if (method.paymentProofImages && method.paymentProofImages.length > 0) {
          allImages.push(...method.paymentProofImages);
        }
      });
      return allImages;
    }
    return order.paymentProofImages ;
  };

  const getStatusBadge = (order) => {
    if (order.verify) {
      return (
        <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
          ✓ Verified
        </span>
      );
    }
    if (order.decline) {
      return (
        <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
          ✗ Declined
        </span>
      );
    }
    return (
      <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">
        Pending
      </span>
    );
  };

  if (isError)
    return <p className="text-red-600 mt-4">Error fetching reverify orders</p>;

  return (
    <div className="w-full mt-6">
      {/* Search */}
      <div className="mb-4 flex justify-between items-center">
        <Input
          className="w-80"
          placeholder="Search by Order Number or Customer Name"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
        <div className="text-sm text-gray-600">
          Total (Verified/Declined): <strong>{orders.length}</strong>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto hide-scrollbar">
        <table className="min-w-[1200px] w-full table-auto border border-gray-200">
          <thead className="bg-gray-100 text-left text-sm font-semibold text-gray-700">
            <tr>
              <th className="p-3">Order Date</th>
              <th className="p-3">Order Number</th>
              <th className="p-3">Customer</th>
              <th className="p-3">Swapnil Gautam</th>
              <th className="p-3">ClicktoCart</th>
              <th className="p-3">Invoice Amount</th>
              <th className="p-3">Status</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white text-sm">
            {isLoading ? (
              <tr>
                <td colSpan={8} className="text-center py-6 text-gray-500">
                  Loading orders...
                </td>
              </tr>
            ) : orders.length > 0 ? (
              orders.map((order, index) => {
                const swapnilAmount = getPaymentByOption(
                  order,
                  "Swapnil Gautam"
                );
                const clickToCartAmount = getPaymentByOption(
                  order,
                  "ClickToCart"
                );
                return (
                  <tr
                    key={order._id || index}
                    className="hover:bg-gray-50 border-b"
                  >
                    <td className="p-4">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </td>
                    <td className="p-4">
                      <div className="font-medium text-blue-600">
                        {order.OrderNumber}
                      </div>
                    </td>
                    <td className="p-4">
                      <p className="font-medium">{order.CustomerName}</p>
                      <p className="text-xs text-gray-500">
                        {order.ShippingAddress?.Contact ||
                          order.BillingAddress?.Contact}
                      </p>
                    </td>
                    <td className="p-4 text-green-600 font-semibold">
                      ₹{swapnilAmount.toFixed(2)}
                    </td>
                    <td className="p-4 text-blue-600 font-semibold">
                      ₹{clickToCartAmount.toFixed(2)}
                    </td>
                    <td className="p-4">
                      ₹{order.InvoiceAmount?.toFixed(2) || 0}
                    </td>
                    <td className="p-4">{getStatusBadge(order)}</td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleViewDetails(order)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded disabled:opacity-50"
                          title="View Details"
                          disabled={isReverifying}
                        >
                          <Eye size={18} />
                        </button>
                        {order.declineRemarks && (
                          <button
                            onClick={() =>
                              handleViewRemark(order.declineRemarks)
                            }
                            className="p-2 text-gray-600 hover:bg-gray-50 rounded"
                            title="View Remark"
                          >
                            <MessageSquareText size={18} />
                          </button>
                        )}
                        <button
                          onClick={() => handleReverify(order)}
                          className="p-2 text-orange-600 hover:bg-orange-50 rounded disabled:opacity-50"
                          title="Edit Payment Methods"
                          disabled={isReverifying}
                        >
                          <RotateCcw size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={8} className="text-center py-6 text-gray-400">
                  No verified or declined orders found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-6 text-sm text-gray-700">
          <p>
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </p>
          <div className="space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1 || isFetching}
              className={`px-4 py-2 rounded border ${
                currentPage === 1 || isFetching
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Previous
            </button>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages || isFetching}
              className={`px-4 py-2 rounded border ${
                currentPage === totalPages || isFetching
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Details Modal */}
      <Modal
        title={`Order Details - ${selectedOrder?.OrderNumber}`}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={800}
      >
        {selectedOrder && (
          <div className="space-y-4">
            {/* Status Info */}
            <div
              className={`rounded p-4 mb-4 ${
                selectedOrder.verify
                  ? "bg-green-50 border border-green-200"
                  : "bg-red-50 border border-red-200"
              }`}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`font-semibold ${
                    selectedOrder.verify ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {selectedOrder.verify
                    ? "✓ Payment Verified"
                    : "✗ Payment Declined"}
                </span>
              </div>
              {selectedOrder.verifiedAt && (
                <p className="text-sm text-gray-600 mt-1">
                  Verified on:{" "}
                  {new Date(selectedOrder.verifiedAt).toLocaleString()}
                </p>
              )}
              {selectedOrder.declinedAt && (
                <p className="text-sm text-gray-600 mt-1">
                  Declined on:{" "}
                  {new Date(selectedOrder.declinedAt).toLocaleString()}
                </p>
              )}
            </div>

            {/* Customer Info */}
            <div className="border-b pb-4">
              <h3 className="font-semibold mb-2">Customer Information</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Name:</span>{" "}
                  <span className="font-medium">
                    {selectedOrder.CustomerName}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Contact:</span>{" "}
                  <span className="font-medium">
                    {selectedOrder.ShippingAddress?.Contact ||
                      selectedOrder.BillingAddress?.Contact}
                  </span>
                </div>
              </div>
            </div>

            {/* Payment Info */}
            <div className="border-b pb-4">
              <h3 className="font-semibold mb-2">Payment Information</h3>
              {selectedOrder.paymentMethods &&
              selectedOrder.paymentMethods.length > 0 ? (
                <div className="space-y-3">
                  {selectedOrder.paymentMethods.map((method, idx) => (
                    <div
                      key={idx}
                      className="p-3 bg-blue-50 rounded border border-blue-200"
                    >
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Payment Option:</span>{" "}
                          <span className="font-medium">
                            {method.paymentOption}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Amount:</span>{" "}
                          <span className="font-medium text-green-600">
                            ₹{method.paymentAmount?.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="p-3 bg-green-50 rounded border border-green-200 font-semibold">
                    Total Payment: ₹
                    {selectedOrder.paymentTotalAmount?.toFixed(2) || 0}
                  </div>
                </div>
              ) : null}
            </div>

            {/* Payment Proof Images */}
            <div>
              <h3 className="font-semibold mb-2">Payment Proof Images</h3>
              {(() => {
                const allImages = getAllPaymentImages(selectedOrder);
                return allImages.length > 0 ? (
                  <div className="grid grid-cols-3 gap-4">
                    {allImages.map((img, index) => (
                      <a
                        key={index}
                        href={`${BACKEND_URL}${img}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="border rounded overflow-hidden hover:shadow-lg transition-shadow"
                      >
                        <img
                          src={`${BACKEND_URL}${img}`}
                          alt={`Payment proof ${index + 1}`}
                          className="w-full h-32 object-cover"
                        />
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400">
                    No payment proof images uploaded
                  </p>
                );
              })()}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-6 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Close
              </button>
              <button
                onClick={() => {
                  handleReverify(selectedOrder);
                  setIsModalOpen(false);
                }}
                disabled={isReverifying}
                className="px-6 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50"
              >
                {isReverifying ? "Resetting..." : "Edit Payment Methods"}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Remark Modal */}
      <Modal
        title="Decline Remark"
        open={isRemarkModalOpen}
        onCancel={() => setIsRemarkModalOpen(false)}
        footer={[
          <button
            key="close"
            onClick={() => setIsRemarkModalOpen(false)}
            className="px-6 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Close
          </button>,
        ]}
      >
        <p className="text-gray-700 whitespace-pre-line">{selectedRemark}</p>
      </Modal>

      {/* Edit Payment Methods Modal */}
      <Modal
        title={`Edit Payment Methods - ${editOrder?.OrderNumber}`}
        open={isEditModalOpen}
        onCancel={handleCloseEditModal}
        footer={null}
        width={900}
      >
        <div className="space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Order Info */}
          <div className="bg-blue-50 border border-blue-200 rounded p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Customer:</span>{" "}
                <span className="font-medium">{editOrder?.CustomerName}</span>
              </div>
              <div>
                <span className="text-gray-600">Invoice Amount:</span>{" "}
                <span className="font-semibold text-green-600">
                  ₹{editOrder?.InvoiceAmount?.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Methods */}
          <div className="space-y-4">
            {editPayments.map((method, index) => (
              <div
                key={index}
                className="border border-gray-300 rounded-lg p-4 bg-gray-50"
              >
                <div className="flex justify-between items-start mb-3">
                  <h4 className="font-semibold text-gray-700">
                    Payment Method {index + 1}
                  </h4>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Option *
                    </label>
                    <Input
                      placeholder="e.g., Swapnil Gautam, Clicktocart, etc."
                      value={method.paymentOption}
                      readOnly
                      onChange={(e) =>
                        handlePaymentChange(
                          index,
                          "paymentOption",
                          e.target.value
                        )
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Amount *
                    </label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={method.paymentAmount}
                      onChange={(e) =>
                        handlePaymentChange(
                          index,
                          "paymentAmount",
                          parseFloat(e.target.value) || 0
                        )
                      }
                      prefix="₹"
                    />
                  </div>
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Proof Images
                  </label>

                  {/* Display images */}
                  {method.paymentProofImages &&
                    method.paymentProofImages.length > 0 && (
                      <div className="grid grid-cols-4 gap-3 mb-3">
                        {method.paymentProofImages.map((img, imgIndex) => (
                          <div
                            key={imgIndex}
                            className="relative border rounded overflow-hidden group"
                          >
                            <img
                              src={
                                img.startsWith("blob:")
                                  ? img
                                  : `${BACKEND_URL}${img}`
                              }
                              alt={`Payment proof ${imgIndex + 1}`}
                              className="w-full h-24 object-cover"
                            />
                            <button
                              onClick={() => handleRemoveImage(index, img)}
                              disabled={isDeleting}
                              className="absolute top-0 right-0 p-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs shadow-md hover:bg-red-700 transition"
                              title="Remove image"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                  {/* Upload button */}
                  <label className="inline-block cursor-pointer">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) => handleImageUpload(index, e)}
                      className="hidden"
                    />
                    <span className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 inline-block text-sm">
                      + Upload Images
                    </span>
                  </label>
                </div>

                {/* Save button for this payment method */}
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => handleSavePaymentMethod(index)}
                    disabled={isUpdating}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    {isUpdating ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Total Summary */}
          <div className="bg-green-50 border border-green-200 rounded p-4">
            <div className="flex justify-between items-center">
              <span className="font-semibold text-gray-700">
                Total Payment Amount:
              </span>
              <span className="text-xl font-bold text-green-600">
                ₹{getTotalPaymentAmount().toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center mt-2 text-sm">
              <span className="text-gray-600">Invoice Amount:</span>
              <span className="font-medium">
                ₹{editOrder?.InvoiceAmount?.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Close Button */}
          {/* Action Buttons */}
          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              onClick={handleCloseEditModal}
              className="px-6 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              Close
            </button>
            <button
              onClick={() => {
                reverifyOrder(editOrder._id, {
                  onSuccess: () => {
                    handleCloseEditModal();
                  },
                });
              }}
              disabled={isReverifying}
              className="px-6 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? "Reverifying..." : "Reverify Order"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ReverifyOrder;
